
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import React, { useContext, useMemo, useRef } from 'react';
import flush from 'just-flush';
import { DateTime } from 'luxon';
import useTranslation from 'next-translate/useTranslation';
import { GetStaticPaths, GetStaticPathsResult, GetStaticProps } from 'next';
import { useQuery } from 'urql';
import { Divider, EventPageIntro, ImageContext, Section, SidebarContent, useSectionNavigation } from '@stereoagency/ui';
import { clientFactory, useFragment } from '@/gql';
import { acfFirst, acfNode, extractHeadingsFromHtml, findInArrayWaterfall, normalizeLocale, scrollTo, withPrepass } from '@/utils';
import { FindAllEventSummariesQueryDocument, FindEventDetailByIdQueryDocument, FindEventSummariesForSlugOrIdQueryDocument, LanguageCodeFilterEnum, } from '@/gql/client/graphql';
import { EventSummaryFieldsFragment, LanguageFieldsFragment, MediaItemFieldsFragment, TermNodeFieldsFragment, } from '@/gql/documents/fragments';
import { i18n } from '@/config';
import { ContentRenderer, Layout, LinesContext, SeoHead } from '@/components';
import { EventFieldsFragment } from '@/gql/documents/fragments/EventFieldsFragment';
import NvisoEventsGrid from '@/components/ContentRenderer/interop/NvisoEventsGrid';
import { ALL_HEADINGS_SELECTOR } from '@/constants';
type UrlParams = {
    slugOrId: string;
};
type PageProps = {
    databaseId: number;
    preview?: boolean;
};
export default function Page({ databaseId, preview }: PageProps) {
    const contentRef = useRef<HTMLDivElement | null>(null);
    const { t } = useTranslation();
    const [{ data }] = useQuery({
        query: FindEventDetailByIdQueryDocument,
        variables: { id: String(databaseId), asPreview: Boolean(preview) }
    });
    const fields = useFragment(EventFieldsFragment, data?.event);
    const summaryFields = useFragment(EventSummaryFieldsFragment, fields);
    const featuredImageFields = useFragment(MediaItemFieldsFragment, summaryFields?.featuredImage?.node);
    const { ImageComponent } = useContext(ImageContext);
    const activeIndex = useSectionNavigation(contentRef, ALL_HEADINGS_SELECTOR);
    const callToAction = useMemo(() => acfNode(summaryFields?.cptEventCustomFields?.callToAction, 'CallToAction') ?? null, [summaryFields]);
    const label = useMemo(() => {
        const category = summaryFields?.categories?.nodes?.[0];
        if (category) {
            return useFragment(TermNodeFieldsFragment, category).name ?? null;
        }
        return null;
    }, [summaryFields]);
    const pretitle = useMemo(() => {
        const datetime = summaryFields?.cptEventCustomFields?.datetime
            ? DateTime.fromISO(summaryFields.cptEventCustomFields.datetime).toFormat('d.M.yyyy')
            : undefined;
        return datetime ?? null;
    }, [summaryFields]);
    const titles = useMemo(() => extractHeadingsFromHtml(fields?.content ?? ''), [fields]);
    const navigationItems = useMemo(() => titles.map((title, index) => ({
        active: index === activeIndex,
        label: title,
        onClick: () => {
            if (contentRef.current) {
                const headings = Array.from(contentRef.current.querySelectorAll<HTMLHeadingElement>(ALL_HEADINGS_SELECTOR));
                const heading = headings.find((h) => h.textContent === title);
                if (heading) {
                    scrollTo(heading);
                }
            }
        }
    })), [titles, activeIndex]);
    if (!data || !fields || !summaryFields)
        return null;
    return (<LinesContext.Provider value={useMemo(() => ({
            enableLines: fields.pageCustomFields?.enableLines ?? false
        }), [summaryFields])}>
      <Layout preview={preview} enableLines={fields.pageCustomFields?.enableLines ?? false} useFixedNavbar={fields.pageCustomFields?.enableOverlayNavbar ?? false} hideFooterDivider={fields.pageCustomFields?.hideFooterDivider ?? false} hideSidetext={fields.pageCustomFields?.hideSidetext ?? false} sidetext={fields.pageCustomFields?.sidetext?.text
            ? {
                text: fields.pageCustomFields.sidetext.text,
                placement: (acfFirst(fields.pageCustomFields.sidetext.placement) ?? 'left') as 'left' | 'right'
            }
            : undefined}>
        <SeoHead seo={fields.seo ?? undefined}/>
        <EventPageIntro label={label ?? undefined} pretitle={pretitle ?? undefined} title={summaryFields.title ?? ''} enableLines={fields.pageCustomFields?.enableLines ?? false}/>
        {featuredImageFields?.mediaItemUrl && (<Section container lines={fields.pageCustomFields?.enableLines ? 'dark' : false}>
            <ImageComponent alt={featuredImageFields.altText ?? ''} src={featuredImageFields.mediaItemUrl} width={featuredImageFields.mediaDetails?.width ?? 1920} height={featuredImageFields.mediaDetails?.height ?? 1080}/>
          </Section>)}
        <SidebarContent enableLines={fields.pageCustomFields?.enableLines ?? false} callToAction={callToAction?.cptCallToActionCustomFields?.href?.url
            ? {
                style: 'link',
                label: callToAction?.title ?? t('pages--event:be-part-of-it'),
                href: callToAction.cptCallToActionCustomFields.href.url,
                target: callToAction.cptCallToActionCustomFields.href.target ?? '/'
            }
            : undefined} navigation={navigationItems}>
          <div ref={contentRef} className="prose md:prose-md">
            <ContentRenderer html={fields.content ?? ''}/>
          </div>
        </SidebarContent>
        <Divider enableLines={fields.pageCustomFields?.enableLines ?? false} variant="full-width" background="transparent"/>
        <NvisoEventsGrid hideIfEmpty backgroundColor="default" exclude={[databaseId]} data={{
            label: t('pages--event:our-events'),
            title: t('pages--event:next-events'),
            limit: 3
        }}/>
      </Layout>
    </LinesContext.Provider>);
}
export const getStaticPaths: GetStaticPaths<UrlParams> = async () => {
    type PathsType = GetStaticPathsResult<UrlParams>['paths'];
    const client = clientFactory();
    const allEvents = await client.query(FindAllEventSummariesQueryDocument, {
        lang: LanguageCodeFilterEnum.All
    }).toPromise();
    const nodes = allEvents.data?.events?.nodes ?? [];
    return {
        paths: flush(nodes.reduce<PathsType>((acc, node) => {
            const fields = useFragment(EventSummaryFieldsFragment, node);
            const language = useFragment(LanguageFieldsFragment, fields?.language);
            if (!fields?.slug)
                return acc;
            acc.push({
                locale: language?.code ? normalizeLocale(language?.code) : i18n.defaultLocale,
                params: { slugOrId: fields.slug! }
            });
            return acc;
        }, [])),
        fallback: 'blocking'
    };
};
const getStaticProps: GetStaticProps<PageProps, UrlParams> = async ({ preview, params, locale }) => {
    if (!params?.slugOrId) {
        return {
            notFound: true
        };
    }
    return withPrepass({
        params,
        preview,
        locale,
        propsFactory: async ({ urqlClient }) => {
            const queryParams = Number.isNaN(Number(params.slugOrId)) ? { slug: params.slugOrId } : { databaseId: Number(params.slugOrId) };
            const { data: eventsQuery, error } = await urqlClient.query(FindEventSummariesForSlugOrIdQueryDocument, queryParams).toPromise();
            if (error) {
                throw error;
            }
            const events = useFragment(EventSummaryFieldsFragment, flush(eventsQuery?.events?.nodes ?? []));
            const event = findInArrayWaterfall(events, [locale ?? i18n.defaultLocale, i18n.defaultLocale], (entry, param) => {
                const language = useFragment(LanguageFieldsFragment, entry.language);
                return Boolean(language?.code && locale === normalizeLocale(param));
            });
            if (!event) {
                return {
                    notFound: true
                };
            }
            return {
                preview: !!preview,
                databaseId: event.databaseId
            };
        },
        Component: Page
    });
};

    async function __Next_Translate__getStaticProps__1a1104ac989__(ctx) {
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/event/[slugOrId]',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig.loadLocaleFrom || ((l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1a1104ac989__ as getStaticProps }
  